/**
 * Store Zustand du funnel "Calculer mes aides".
 *
 * Persistance localStorage : permet de reprendre un parcours interrompu (F5 safe).
 * Tous les états de progression sont sauvegardés (sauf les inputs sensibles
 * — RGPD : email, téléphone, adresse — qui ne sont persistés que si l'utilisateur
 * a atteint l'étape contact en local).
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CommuneResult } from '../../ui/city-select';
import type { StoredLocation } from '../../../lib/audience-storage';

/* ─── Types ──────────────────────────────────────────────── */

export type ProfileType = 'occupant' | 'bailleur' | 'copropriete' | 'locataire';
export type LogementType = 'maison' | 'appartement' | 'copropriete';
export type FunnelStep = 0 | 1 | 2 | 3 | 4 | 5;

export interface ContactInfo {
  firstName: string;
  lastName: string;
  phoneCountry: string; // ISO2
  phoneDial: string;
  phoneNational: string;
  email: string;
  address: string;
  postalCode: string;
}

export interface AidsFunnelState {
  /** Index 0-based de l'étape courante. 0..5 → 6 étapes au total. */
  step: FunnelStep;
  /** Direction de transition pour les animations (1 = forward, -1 = backward). */
  dir: 1 | -1;

  /* — Step 1 : profil + ville — */
  profile: ProfileType | null;
  location: StoredLocation | null;

  /* — Step 2 : logement — */
  logement: LogementType | null;
  /** Étiquette DPE actuelle (G, F, E, D, C, B, A) — optionnelle, enrichit le résultat */
  dpeActuel: string | null;
  /** Facture énergie annuelle actuelle (€/an) — optionnelle, permet de calculer le ROI */
  energyBillEur: number | null;

  /* — Step 3 : revenus — */
  rfr: number | null;
  householdSize: number | null;

  /* — Step 4 : travaux — */
  travaux: string[];

  /* — Step 5 : contact — */
  contact: ContactInfo;

  /* — Step 6 : résultat — */
  leadId: string | null;
  submitting: boolean;
  submitError: string | null;
}

interface AidsFunnelActions {
  setStep: (step: FunnelStep, dir?: 1 | -1) => void;
  goNext: () => void;
  goBack: () => void;
  reset: () => void;

  setProfile: (p: ProfileType) => void;
  setLocation: (l: StoredLocation | null) => void;
  setLocationFromCommune: (c: CommuneResult | null) => void;

  setLogement: (l: LogementType) => void;
  setDpeActuel: (dpe: string | null) => void;
  setEnergyBillEur: (eur: number | null) => void;

  setRfr: (rfr: number | null) => void;
  setHouseholdSize: (n: number) => void;

  toggleTravail: (id: string) => void;
  setTravaux: (ids: string[]) => void;

  patchContact: (patch: Partial<ContactInfo>) => void;

  setSubmitting: (v: boolean) => void;
  setSubmitError: (msg: string | null) => void;
  setLeadId: (id: string | null) => void;
}

const INITIAL_CONTACT: ContactInfo = {
  firstName: '',
  lastName: '',
  phoneCountry: 'FR',
  phoneDial: '33',
  phoneNational: '',
  email: '',
  address: '',
  postalCode: '',
};

const INITIAL_STATE: AidsFunnelState = {
  step: 0,
  dir: 1,
  profile: null,
  location: null,
  logement: null,
  dpeActuel: null,
  energyBillEur: null,
  rfr: null,
  householdSize: null,
  travaux: [],
  contact: INITIAL_CONTACT,
  leadId: null,
  submitting: false,
  submitError: null,
};

/* ─── Helpers ────────────────────────────────────────────── */

/**
 * Lazy-loaded zoning helper — évite un import top-level qui force le chargement
 * de la lib de zoning même quand le funnel n'est pas monté.
 */
async function enrichCommune(c: CommuneResult): Promise<StoredLocation> {
  const { getZoning } = await import('../../../lib/zoning');
  return { ...c, ...getZoning(c.codeDepartement) };
}

/* ─── Store ──────────────────────────────────────────────── */

const STORAGE_KEY = 'sef_aids_funnel_v1';

export const useAidsStore = create<AidsFunnelState & AidsFunnelActions>()(
  persist(
    (set, get) => ({
      ...INITIAL_STATE,

      setStep: (step, dir) =>
        set((s) => ({
          step,
          dir: dir ?? (step > s.step ? 1 : -1),
        })),

      goNext: () => {
        const next = Math.min(5, (get().step + 1) as FunnelStep) as FunnelStep;
        set({ step: next, dir: 1 });
      },

      goBack: () => {
        const prev = Math.max(0, (get().step - 1) as FunnelStep) as FunnelStep;
        set({ step: prev, dir: -1 });
      },

      reset: () => set({ ...INITIAL_STATE }),

      setProfile: (p) =>
        set((s) => {
          // Copropriété force le type de logement
          const logement: LogementType | null =
            p === 'copropriete' ? 'copropriete' : s.logement;
          // Reset des revenus si profil sans besoin
          const resetIncome = p === 'copropriete' || p === 'locataire';
          return {
            profile: p,
            logement,
            rfr: resetIncome ? null : s.rfr,
            householdSize: resetIncome ? null : s.householdSize,
          };
        }),

      setLocation: (l) => set({ location: l }),

      setLocationFromCommune: (c) => {
        if (!c) {
          set({ location: null });
          return;
        }
        void enrichCommune(c).then((enriched) => set({ location: enriched }));
      },

      setLogement: (l) => set({ logement: l }),
      setDpeActuel: (dpe) => set({ dpeActuel: dpe }),
      setEnergyBillEur: (eur) => set({ energyBillEur: eur }),

      setRfr: (rfr) => set({ rfr }),
      setHouseholdSize: (n) =>
        set({ householdSize: Math.max(1, Math.min(n, 12)) }),

      toggleTravail: (id) =>
        set((s) => {
          if (s.travaux.includes(id)) {
            return { travaux: s.travaux.filter((x) => x !== id) };
          }
          return { travaux: [...s.travaux, id] };
        }),

      setTravaux: (ids) => set({ travaux: ids }),

      patchContact: (patch) =>
        set((s) => ({ contact: { ...s.contact, ...patch } })),

      setSubmitting: (v) => set({ submitting: v }),
      setSubmitError: (msg) => set({ submitError: msg }),
      setLeadId: (id) => set({ leadId: id }),
    }),
    {
      name: STORAGE_KEY,
      // On ne persiste pas les états transitoires (transitions, soumission, ID lead serveur).
      partialize: (s) => ({
        step: s.step,
        profile: s.profile,
        location: s.location,
        logement: s.logement,
        dpeActuel: s.dpeActuel,
        energyBillEur: s.energyBillEur,
        rfr: s.rfr,
        householdSize: s.householdSize,
        travaux: s.travaux,
        contact: s.contact,
      }),
    },
  ),
);

/* ─── Step labels (dynamique selon profil) ───────────────── */

export const ALL_STEP_LABELS: Record<number, string> = {
  0: 'Profil',
  1: 'Logement',
  2: 'Revenus',
  3: 'Travaux',
  4: 'Contact',
  5: 'Résultat',
};

/**
 * Profils sans besoin de saisir les revenus.
 * (Copropriété = forfait collectif · Locataire = primes CEE seules.)
 */
export function profileNeedsRevenus(p: ProfileType | null): boolean {
  if (!p) return true;
  return p === 'occupant' || p === 'bailleur';
}

/**
 * Libellé « Étape X sur Y » tenant compte de l'étape Revenus
 * éventuellement masquée (copropriété / locataire → 5 étapes au lieu de 6).
 */
export function aidsStepCounterLabel(step: FunnelStep, profile: ProfileType | null): string {
  const needsRevenus = profileNeedsRevenus(profile);
  const total = needsRevenus ? 6 : 5;
  const current = !needsRevenus && step > 2 ? step : step + 1;
  return `Étape ${current} sur ${total}`;
}
